import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BESOINS = [
  { value: "hebergement-urgence", label: "Hébergement d'urgence" },
  { value: "logement-saisonnier", label: "Logement saisonnier" },
  { value: "salaries", label: "Logement de salariés" },
  { value: "location", label: "Location" },
  { value: "autre", label: "Autre" },
];

const initial = {
  nom: "",
  structure: "",
  email: "",
  telephone: "",
  type_besoin: "",
  nombre_personnes: "",
  localisation: "",
  message: "",
};

export const ContactForm = () => {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Pre-fill type_besoin if requested from another section (e.g. Location button)
  useEffect(() => {
    try {
      const preset = sessionStorage.getItem("yono_form_type");
      if (preset) {
        setData((d) => ({ ...d, type_besoin: preset }));
        sessionStorage.removeItem("yono_form_type");
      }
    } catch (e) {}

    const handler = (e) => {
      const type = e?.detail?.type;
      if (type) {
        setData((d) => ({ ...d, type_besoin: type }));
        setDone(false);
      }
    };
    window.addEventListener("yono:prefill-type", handler);
    return () => window.removeEventListener("yono:prefill-type", handler);
  }, []);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.nom || !data.email || !data.type_besoin) {
      toast.error("Merci de renseigner le nom, l'email et le type de besoin.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, data);
      toast.success(res.data.message || "Demande envoyée — merci.");
      setDone(true);
      setData(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Une erreur est survenue. Merci de réessayer.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-[var(--yono-border)] bg-white p-10 lg:p-14"
        data-testid="contact-success"
      >
        <div className="eyebrow text-[var(--yono-wood)] mb-3">
          Demande reçue
        </div>
        <h3 className="font-heading text-[28px] lg:text-[34px] leading-[1.15] mb-5">
          Merci, votre demande nous est bien parvenue.
        </h3>
        <p className="text-[15px] leading-[1.8] text-[var(--yono-warm-grey)] max-w-xl">
          Nous étudions votre besoin et reviendrons vers vous rapidement pour
          vous proposer une configuration du YONO TWIN adaptée à votre projet.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="btn-outline mt-8"
          data-testid="contact-new-request-btn"
        >
          Nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-[var(--yono-border)] bg-white p-8 lg:p-12"
      data-testid="contact-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="yono-label" htmlFor="nom">Nom / prénom *</label>
          <input
            id="nom"
            type="text"
            value={data.nom}
            onChange={set("nom")}
            className="yono-input"
            required
            data-testid="form-input-nom"
          />
        </div>
        <div>
          <label className="yono-label" htmlFor="structure">Structure</label>
          <input
            id="structure"
            type="text"
            value={data.structure}
            onChange={set("structure")}
            className="yono-input"
            placeholder="Association, collectivité, entreprise…"
            data-testid="form-input-structure"
          />
        </div>
        <div>
          <label className="yono-label" htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            value={data.email}
            onChange={set("email")}
            className="yono-input"
            required
            data-testid="form-input-email"
          />
        </div>
        <div>
          <label className="yono-label" htmlFor="telephone">Téléphone</label>
          <input
            id="telephone"
            type="tel"
            value={data.telephone}
            onChange={set("telephone")}
            className="yono-input"
            data-testid="form-input-telephone"
          />
        </div>
        <div>
          <label className="yono-label" htmlFor="type_besoin">Type de besoin *</label>
          <select
            id="type_besoin"
            value={data.type_besoin}
            onChange={set("type_besoin")}
            className="yono-input"
            required
            data-testid="form-input-type-besoin"
          >
            <option value="">— Sélectionner —</option>
            {BESOINS.map((b) => (
              <option key={b.value} value={b.label}>
                {b.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="yono-label" htmlFor="nombre_personnes">
            Nombre de personnes à héberger
          </label>
          <input
            id="nombre_personnes"
            type="text"
            value={data.nombre_personnes}
            onChange={set("nombre_personnes")}
            className="yono-input"
            placeholder="Ex : 8, 24, 50+"
            data-testid="form-input-nombre"
          />
        </div>
        <div className="md:col-span-2">
          <label className="yono-label" htmlFor="localisation">
            Localisation du site
          </label>
          <input
            id="localisation"
            type="text"
            value={data.localisation}
            onChange={set("localisation")}
            className="yono-input"
            placeholder="Commune, département, région"
            data-testid="form-input-localisation"
          />
        </div>
        <div className="md:col-span-2">
          <label className="yono-label" htmlFor="message">Message libre</label>
          <textarea
            id="message"
            rows={5}
            value={data.message}
            onChange={set("message")}
            className="yono-input resize-none"
            placeholder="Durée d'utilisation, sanitaires communs disponibles, contraintes techniques, calendrier souhaité…"
            data-testid="form-input-message"
          />
        </div>
      </div>

      <p className="mt-6 text-[12px] text-[var(--yono-warm-grey)] leading-relaxed">
        Les champs marqués d'un astérisque (*) sont obligatoires. Vos
        informations sont utilisées uniquement pour traiter votre demande.
      </p>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="form-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Envoi en cours…
          </>
        ) : (
          <>
            Demander une étude personnalisée
            <ArrowRight size={14} />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
